import React, { useState } from 'react';

interface TemplateField {
  key: string;
  label: string;
  defaultValue: string;
  type?: 'text' | 'textarea' | 'color';
}

interface EmailTheme {
  name: string;
  primary: string;
  secondary: string;
  accent: string;
  bg: string;
}

interface EmailTemplate {
  name: string;
  icon: string;
  fields: TemplateField[];
  generate: (values: Record<string, string>, theme: EmailTheme) => string;
}

const THEMES: EmailTheme[] = [
  { name: 'Professional', primary: '#1a365d', secondary: '#2d3748', accent: '#3182ce', bg: '#f7fafc' },
  { name: 'Modern Purple', primary: '#553c9a', secondary: '#6b46c1', accent: '#9f7aea', bg: '#faf5ff' },
  { name: 'Ocean Blue', primary: '#0c4a6e', secondary: '#075985', accent: '#0ea5e9', bg: '#f0f9ff' },
  { name: 'Sunset Red', primary: '#7f1d1d', secondary: '#991b1b', accent: '#ef4444', bg: '#fef2f2' },
  { name: 'Nature Green', primary: '#14532d', secondary: '#166534', accent: '#22c55e', bg: '#f0fdf4' },
  { name: 'Midnight Dark', primary: '#0f172a', secondary: '#1e293b', accent: '#60a5fa', bg: '#1e293b' },
  { name: 'Electric Orange', primary: '#7c2d12', secondary: '#c2410c', accent: '#f97316', bg: '#fff7ed' },
  { name: 'Corporate Gray', primary: '#1f2937', secondary: '#374151', accent: '#6b7280', bg: '#f9fafb' },
  { name: 'Rose Gold', primary: '#831843', secondary: '#be185d', accent: '#f472b6', bg: '#fdf2f8' },
  { name: 'Teal Fresh', primary: '#134e4a', secondary: '#115e59', accent: '#14b8a6', bg: '#f0fdfa' },
];

const generateBaseHtml = (content: string, theme: EmailTheme) => `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background-color:${theme.bg};">
<div style="max-width:600px;margin:0 auto;padding:20px;">
${content}
</div></body></html>`;

const TEMPLATES: EmailTemplate[] = [
  // 1. TEMPLATE MODULAR COMPLET (stil email_scheduler - cu header, warning, status, features, footer)
  {
    name: 'Email Complet', icon: '💎',
    fields: [
      { key: 'emoji', label: 'Emoji header', defaultValue: '💳' },
      { key: 'title', label: 'Titlu principal', defaultValue: 'NOTIFICARE IMPORTANTA' },
      { key: 'subtitle', label: 'Subtitlu', defaultValue: 'Actiune necesara din partea dvs.' },
      { key: 'warningText', label: 'Avertisment (gol = ascuns)', defaultValue: 'Termen limita aproape - va rugam sa actionati' },
      { key: 'companyName', label: 'Companie', defaultValue: 'Firma Mea SRL' },
      { key: 'refNumber', label: 'Nr. referinta', defaultValue: 'REF-2026-001' },
      { key: 'dueDate', label: 'Termen', defaultValue: '15.04.2026' },
      { key: 'amount', label: 'Suma/Valoare', defaultValue: '100 EUR' },
      { key: 'status', label: 'Badge status', defaultValue: 'URGENT' },
      { key: 'alertTitle', label: 'Titlu alert', defaultValue: 'Detalii importante' },
      { key: 'alertMessage', label: 'Mesaj alert', defaultValue: 'Va rugam sa verificati datele si sa confirmati in cel mai scurt timp posibil.' },
      { key: 'btnText', label: 'Text buton', defaultValue: 'Actioneaza Acum' },
      { key: 'btnLink', label: 'Link buton', defaultValue: 'https://example.com/action' },
      { key: 'feat1Icon', label: 'Feature 1 icon', defaultValue: '🛡️' },
      { key: 'feat1Title', label: 'Feature 1 titlu', defaultValue: 'Securitate' },
      { key: 'feat1Sub', label: 'Feature 1 subtitlu', defaultValue: 'Date protejate' },
      { key: 'feat2Icon', label: 'Feature 2 icon', defaultValue: '⚡' },
      { key: 'feat2Title', label: 'Feature 2 titlu', defaultValue: 'Rapid' },
      { key: 'feat2Sub', label: 'Feature 2 subtitlu', defaultValue: 'Procesare instant' },
      { key: 'feat3Icon', label: 'Feature 3 icon', defaultValue: '📞' },
      { key: 'feat3Title', label: 'Feature 3 titlu', defaultValue: 'Suport' },
      { key: 'feat3Sub', label: 'Feature 3 subtitlu', defaultValue: 'Non-stop 24/7' },
      { key: 'phone', label: 'Telefon contact', defaultValue: '+40 123 456 789' },
      { key: 'email', label: 'Email contact', defaultValue: 'contact@firma.ro' },
    ],
    generate: (v, t) => generateBaseHtml(`
<div style="background:linear-gradient(135deg,${t.primary},${t.secondary});padding:32px;border-radius:12px 12px 0 0;color:white;text-align:center;">
  <div style="font-size:44px;margin-bottom:8px;">${v.emoji}</div>
  <h1 style="margin:0;font-size:26px;letter-spacing:2px;">${v.title}</h1>
  <p style="margin:6px 0 0;opacity:0.85;font-size:14px;">${v.subtitle}</p>
</div>
<div style="height:3px;background:linear-gradient(90deg,${t.accent},${t.primary},${t.accent});"></div>
${v.warningText ? `<div style="background:#fef3cd;border-left:4px solid #f59e0b;padding:12px 16px;margin:0;">
  <span style="font-size:13px;color:#92400e;">⚠️ ${v.warningText}</span>
</div>` : ''}
<div style="background:white;padding:28px;border-radius:0 0 12px 12px;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
  ${v.companyName || v.refNumber || v.dueDate ? `<div style="display:flex;justify-content:space-between;padding:12px 16px;background:${t.bg};border-radius:8px;margin-bottom:16px;font-size:13px;">
    <div>${v.companyName ? `<div style="color:#999;font-size:11px;">Companie</div><div style="font-weight:600;">${v.companyName}</div>` : ''}</div>
    <div style="text-align:center;">${v.refNumber ? `<div style="color:#999;font-size:11px;">Referinta</div><div style="font-weight:600;">${v.refNumber}</div>` : ''}</div>
    <div style="text-align:right;">${v.dueDate ? `<div style="color:#999;font-size:11px;">Termen</div><div style="font-weight:600;">${v.dueDate}</div>` : ''}</div>
  </div>` : ''}
  <div style="display:flex;gap:12px;margin-bottom:20px;">
    ${v.amount ? `<div style="flex:1;background:linear-gradient(135deg,${t.primary},${t.secondary});border-radius:10px;padding:18px;text-align:center;color:white;">
      <div style="font-size:11px;opacity:0.8;margin-bottom:4px;">VALOARE</div>
      <div style="font-size:26px;font-weight:bold;">${v.amount}</div>
    </div>` : ''}
    ${v.status ? `<div style="flex:1;background:linear-gradient(135deg,${t.accent},${t.primary});border-radius:10px;padding:18px;text-align:center;color:white;">
      <div style="font-size:11px;opacity:0.8;margin-bottom:4px;">STATUS</div>
      <div style="font-size:22px;font-weight:bold;">${v.status}</div>
    </div>` : ''}
  </div>
  <div style="background:${t.bg};border-left:4px solid ${t.accent};border-radius:0 8px 8px 0;padding:16px;margin-bottom:20px;">
    <div style="font-weight:600;margin-bottom:6px;color:${t.primary};">✅ ${v.alertTitle}</div>
    <div style="font-size:13px;color:#555;line-height:1.6;">${v.alertMessage}</div>
  </div>
  <div style="text-align:center;margin:24px 0;">
    <a href="${v.btnLink}" style="display:inline-block;padding:14px 36px;background:linear-gradient(135deg,${t.accent},${t.primary});color:white;text-decoration:none;border-radius:8px;font-weight:700;font-size:15px;box-shadow:0 4px 12px ${t.accent}44;">${v.btnText}</a>
  </div>
  <div style="display:flex;gap:8px;margin:24px 0;">
    ${[{i: v.feat1Icon, t: v.feat1Title, s: v.feat1Sub}, {i: v.feat2Icon, t: v.feat2Title, s: v.feat2Sub}, {i: v.feat3Icon, t: v.feat3Title, s: v.feat3Sub}].map(f => `
    <div style="flex:1;text-align:center;padding:16px 8px;background:${t.bg};border-radius:8px;">
      <div style="font-size:24px;margin-bottom:6px;">${f.i}</div>
      <div style="font-weight:600;font-size:12px;color:${t.primary};">${f.t}</div>
      <div style="font-size:11px;color:#888;margin-top:2px;">${f.s}</div>
    </div>`).join('')}
  </div>
  <hr style="border:none;border-top:1px solid #eee;margin:20px 0;">
  <div style="text-align:center;font-size:12px;color:#999;">
    ${v.phone ? `<span>📞 ${v.phone}</span>` : ''} ${v.phone && v.email ? '&nbsp;&nbsp;|&nbsp;&nbsp;' : ''} ${v.email ? `<span>✉️ ${v.email}</span>` : ''}
  </div>
</div>`, t)
  },
  // 2. CONFIRMARE COMANDA (unic)
  {
    name: 'Confirmare Comanda', icon: '🛒',
    fields: [
      { key: 'name', label: 'Nume client', defaultValue: 'Elena Dumitrescu' },
      { key: 'order_nr', label: 'Nr. Comanda', defaultValue: '#CMD-98765' },
      { key: 'item1', label: 'Produs 1', defaultValue: 'Laptop Pro 15" - 4,299 EUR' },
      { key: 'item2', label: 'Produs 2', defaultValue: 'Mouse Wireless - 149 EUR' },
      { key: 'item3', label: 'Produs 3 (gol = ascuns)', defaultValue: 'Husa laptop - 89 EUR' },
      { key: 'shipping', label: 'Cost livrare', defaultValue: 'GRATUIT' },
      { key: 'total', label: 'Total', defaultValue: '4,537.00 EUR' },
      { key: 'address', label: 'Adresa livrare', defaultValue: 'Str. Victoriei 42, Bucuresti' },
      { key: 'delivery', label: 'Livrare estimata', defaultValue: '29-31 Martie 2026' },
      { key: 'track_link', label: 'Link tracking', defaultValue: 'https://example.com/track' },
    ],
    generate: (v, t) => generateBaseHtml(`
<div style="background:linear-gradient(135deg,${t.primary},${t.accent});padding:32px;border-radius:12px 12px 0 0;color:white;text-align:center;">
  <div style="font-size:48px;">✅</div>
  <h1 style="margin:8px 0 0;font-size:24px;">Comanda Confirmata!</h1>
  <p style="margin:4px 0 0;opacity:0.9;font-size:18px;">${v.order_nr}</p>
</div>
<div style="background:white;padding:28px;border-radius:0 0 12px 12px;box-shadow:0 4px 20px rgba(0,0,0,0.08);">
  <p>Salut <strong>${v.name}</strong>, iti multumim pentru comanda!</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0;">
    <tr style="background:${t.bg};"><td style="padding:10px;font-weight:600;" colspan="2">Produse comandate</td></tr>
    ${[v.item1, v.item2, v.item3].filter(Boolean).map(item => {
      const p = item.split(' - ');
      return `<tr><td style="padding:8px 10px;border-bottom:1px solid #f0f0f0;">${p[0]}</td><td style="padding:8px 10px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:500;">${p[1] || ''}</td></tr>`;
    }).join('')}
    <tr><td style="padding:8px 10px;color:#888;">Livrare</td><td style="padding:8px 10px;text-align:right;color:${t.accent};font-weight:600;">${v.shipping}</td></tr>
    <tr style="background:${t.bg};"><td style="padding:12px;font-weight:bold;font-size:16px;">TOTAL</td><td style="padding:12px;text-align:right;font-weight:bold;font-size:18px;color:${t.accent};">${v.total}</td></tr>
  </table>
  <div style="display:flex;gap:12px;margin:20px 0;">
    <div style="flex:1;background:${t.bg};border-radius:8px;padding:14px;">
      <div style="font-size:11px;color:#999;">📍 Adresa livrare</div>
      <div style="font-weight:500;margin-top:4px;font-size:13px;">${v.address}</div>
    </div>
    <div style="flex:1;background:${t.bg};border-radius:8px;padding:14px;">
      <div style="font-size:11px;color:#999;">🚚 Livrare estimata</div>
      <div style="font-weight:500;margin-top:4px;font-size:13px;">${v.delivery}</div>
    </div>
  </div>
  <div style="text-align:center;margin-top:20px;">
    <a href="${v.track_link}" style="display:inline-block;padding:12px 32px;background:${t.accent};color:white;text-decoration:none;border-radius:8px;font-weight:600;">Urmareste Comanda</a>
  </div>
</div>`, t)
  },
  // 3. INVITATIE EVENIMENT (unic)
  {
    name: 'Invitatie Eveniment', icon: '🎪',
    fields: [
      { key: 'event_name', label: 'Nume eveniment', defaultValue: 'Tech Conference 2026' },
      { key: 'tagline', label: 'Slogan', defaultValue: 'Viitorul tehnologiei incepe aici' },
      { key: 'date', label: 'Data', defaultValue: '15 Aprilie 2026' },
      { key: 'time', label: 'Ora', defaultValue: '09:00 - 18:00' },
      { key: 'location', label: 'Locatie', defaultValue: 'Palatul Parlamentului, Sala Mare' },
      { key: 'speaker1', label: 'Speaker 1', defaultValue: '🎤 Dr. Ana Popa - AI & Machine Learning' },
      { key: 'speaker2', label: 'Speaker 2', defaultValue: '🎤 Ing. Mihai Radu - Cloud Architecture' },
      { key: 'speaker3', label: 'Speaker 3', defaultValue: '🎤 Prof. Dana Iliescu - Cybersecurity' },
      { key: 'price', label: 'Pret bilet', defaultValue: 'Intrare GRATUITA' },
      { key: 'spots', label: 'Locuri disponibile', defaultValue: '127 locuri ramase' },
      { key: 'register_link', label: 'Link inregistrare', defaultValue: 'https://example.com/register' },
    ],
    generate: (v, t) => generateBaseHtml(`
<div style="background:linear-gradient(135deg,${t.primary},${t.secondary});padding:40px 30px;border-radius:12px 12px 0 0;color:white;text-align:center;">
  <h1 style="margin:0;font-size:28px;letter-spacing:1px;">${v.event_name}</h1>
  <p style="margin:8px 0 0;font-size:15px;opacity:0.9;font-style:italic;">${v.tagline}</p>
  <div style="display:flex;justify-content:center;gap:24px;margin-top:20px;font-size:14px;">
    <span>📅 ${v.date}</span>
    <span>🕐 ${v.time}</span>
  </div>
</div>
<div style="background:white;padding:28px;border-radius:0 0 12px 12px;box-shadow:0 4px 20px rgba(0,0,0,0.08);">
  <div style="background:${t.bg};border-radius:8px;padding:14px;margin-bottom:20px;text-align:center;">
    <div style="font-size:12px;color:#999;">📍 Locatie</div>
    <div style="font-weight:600;color:${t.primary};margin-top:4px;">${v.location}</div>
  </div>
  <h3 style="color:${t.primary};margin:0 0 12px;font-size:15px;">Speakeri confirmati</h3>
  ${[v.speaker1, v.speaker2, v.speaker3].filter(Boolean).map(s => `<div style="padding:10px 14px;margin:6px 0;background:${t.bg};border-radius:6px;font-size:13px;">${s}</div>`).join('')}
  <div style="display:flex;gap:12px;margin:24px 0;">
    <div style="flex:1;text-align:center;padding:16px;background:linear-gradient(135deg,${t.accent}15,${t.primary}15);border-radius:10px;border:1px solid ${t.accent}33;">
      <div style="font-size:20px;font-weight:bold;color:${t.accent};">${v.price}</div>
    </div>
    <div style="flex:1;text-align:center;padding:16px;background:linear-gradient(135deg,${t.primary}15,${t.accent}15);border-radius:10px;border:1px solid ${t.primary}33;">
      <div style="font-size:14px;font-weight:600;color:${t.primary};">🔥 ${v.spots}</div>
    </div>
  </div>
  <div style="text-align:center;">
    <a href="${v.register_link}" style="display:inline-block;padding:16px 48px;background:linear-gradient(135deg,${t.accent},${t.primary});color:white;text-decoration:none;border-radius:10px;font-weight:700;font-size:16px;box-shadow:0 4px 16px ${t.accent}44;">Inscrie-te Acum</a>
  </div>
</div>`, t)
  },
  // 4. NEWSLETTER (diferit de cel vechi)
  {
    name: 'Newsletter', icon: '📰',
    fields: [
      { key: 'brand', label: 'Brand/Publicatie', defaultValue: 'TechDigest' },
      { key: 'edition', label: 'Editia', defaultValue: 'Nr. 42 | Martie 2026' },
      { key: 'hero_title', label: 'Articol principal', defaultValue: 'Inteligenta Artificiala in 2026' },
      { key: 'hero_desc', label: 'Descriere principala', defaultValue: 'Cum schimba AI modul in care lucram, invatam si traim. O analiza completa a tendintelor actuale.' },
      { key: 'news1', label: 'Stire 1', defaultValue: '🚀 Startup-ul romanesc X a primit $10M finantare' },
      { key: 'news2', label: 'Stire 2', defaultValue: '📱 Noul update iOS aduce functii revolutionare' },
      { key: 'news3', label: 'Stire 3', defaultValue: '🌍 Conferinta Europeana de Tech - inscrieri deschise' },
      { key: 'quote', label: 'Citat saptamanii', defaultValue: '"Viitorul apartine celor care cred in frumusetea viselor lor." - Eleanor Roosevelt' },
      { key: 'cta_link', label: 'Link citeste mai mult', defaultValue: 'https://example.com/blog' },
    ],
    generate: (v, t) => generateBaseHtml(`
<div style="background:${t.primary};padding:20px 28px;border-radius:12px 12px 0 0;color:white;display:flex;justify-content:space-between;align-items:center;">
  <h2 style="margin:0;font-size:22px;">${v.brand}</h2>
  <span style="font-size:12px;opacity:0.7;">${v.edition}</span>
</div>
<div style="background:white;border-radius:0 0 12px 12px;box-shadow:0 4px 20px rgba(0,0,0,0.08);overflow:hidden;">
  <div style="background:linear-gradient(135deg,${t.primary}10,${t.accent}20);padding:28px;">
    <h2 style="margin:0 0 10px;color:${t.primary};font-size:22px;">${v.hero_title}</h2>
    <p style="margin:0;color:#555;font-size:14px;line-height:1.7;">${v.hero_desc}</p>
    <a href="${v.cta_link}" style="display:inline-block;margin-top:14px;color:${t.accent};font-weight:600;font-size:13px;text-decoration:none;">Citeste mai mult →</a>
  </div>
  <div style="padding:0 28px;">
    <h3 style="color:${t.primary};font-size:14px;margin:20px 0 12px;">📋 In aceasta editie</h3>
    ${[v.news1, v.news2, v.news3].filter(Boolean).map(n => `<div style="padding:10px 0;border-bottom:1px solid #f0f0f0;font-size:13px;color:#444;">${n}</div>`).join('')}
  </div>
  <div style="margin:20px 28px;background:${t.bg};border-left:3px solid ${t.accent};border-radius:0 8px 8px 0;padding:16px;">
    <div style="font-size:13px;color:#666;font-style:italic;line-height:1.6;">${v.quote}</div>
  </div>
  <div style="padding:16px 28px 20px;text-align:center;border-top:1px solid #f0f0f0;">
    <a href="${v.cta_link}" style="display:inline-block;padding:10px 28px;background:${t.accent};color:white;text-decoration:none;border-radius:6px;font-weight:600;font-size:13px;">Viziteaza Blogul</a>
  </div>
</div>`, t)
  },
  // 5. RAPORT STATUS (unic)
  {
    name: 'Raport Status', icon: '📊',
    fields: [
      { key: 'project', label: 'Proiect', defaultValue: 'Platforma E-Commerce v3.0' },
      { key: 'period', label: 'Perioada', defaultValue: 'Saptamana 17-23 Martie 2026' },
      { key: 'status', label: 'Status general', defaultValue: 'Pe drumul cel bun' },
      { key: 'progress', label: 'Progres (%)', defaultValue: '72' },
      { key: 'done1', label: 'Realizat 1', defaultValue: 'Implementare sistem plati online' },
      { key: 'done2', label: 'Realizat 2', defaultValue: 'Testare modul cos cumparaturi' },
      { key: 'done3', label: 'Realizat 3', defaultValue: 'Fix 12 bug-uri critice' },
      { key: 'todo1', label: 'De facut 1', defaultValue: 'Integrare curier' },
      { key: 'todo2', label: 'De facut 2', defaultValue: 'Optimizare performanta mobile' },
      { key: 'blocker', label: 'Blocaje (gol = fara)', defaultValue: '' },
      { key: 'next_meeting', label: 'Urmatoarea intalnire', defaultValue: 'Luni, 24 Martie, 10:00' },
    ],
    generate: (v, t) => generateBaseHtml(`
<div style="background:linear-gradient(135deg,${t.primary},${t.secondary});padding:28px;border-radius:12px 12px 0 0;color:white;">
  <div style="display:flex;justify-content:space-between;align-items:center;">
    <div>
      <h1 style="margin:0;font-size:20px;">📊 Raport Status</h1>
      <p style="margin:4px 0 0;opacity:0.85;font-size:14px;">${v.project}</p>
    </div>
    <div style="background:rgba(255,255,255,0.2);padding:6px 14px;border-radius:20px;font-size:12px;">${v.period}</div>
  </div>
</div>
<div style="background:white;padding:28px;border-radius:0 0 12px 12px;box-shadow:0 4px 20px rgba(0,0,0,0.08);">
  <div style="display:flex;gap:12px;margin-bottom:20px;">
    <div style="flex:1;background:${t.bg};border-radius:10px;padding:18px;text-align:center;">
      <div style="font-size:11px;color:#999;">STATUS</div>
      <div style="font-size:14px;font-weight:600;color:${t.accent};margin-top:4px;">${v.status}</div>
    </div>
    <div style="flex:1;background:${t.bg};border-radius:10px;padding:18px;text-align:center;">
      <div style="font-size:11px;color:#999;">PROGRES</div>
      <div style="font-size:24px;font-weight:bold;color:${t.primary};margin-top:4px;">${v.progress}%</div>
    </div>
  </div>
  <div style="background:#f0fdf4;border-radius:8px;padding:2px 16px 12px;margin-bottom:14px;">
    <h4 style="color:#166534;font-size:13px;">✅ Realizat</h4>
    ${[v.done1, v.done2, v.done3].filter(Boolean).map(d => `<div style="font-size:13px;color:#333;padding:3px 0;">• ${d}</div>`).join('')}
  </div>
  <div style="background:#fef3c7;border-radius:8px;padding:2px 16px 12px;margin-bottom:14px;">
    <h4 style="color:#92400e;font-size:13px;">📋 De facut</h4>
    ${[v.todo1, v.todo2].filter(Boolean).map(d => `<div style="font-size:13px;color:#333;padding:3px 0;">○ ${d}</div>`).join('')}
  </div>
  ${v.blocker ? `<div style="background:#fef2f2;border-radius:8px;padding:2px 16px 12px;margin-bottom:14px;">
    <h4 style="color:#991b1b;font-size:13px;">🚧 Blocaje</h4>
    <div style="font-size:13px;color:#333;">${v.blocker}</div>
  </div>` : ''}
  <div style="text-align:center;padding-top:12px;border-top:1px solid #eee;font-size:13px;color:#666;">
    📅 Urmatoarea intalnire: <strong>${v.next_meeting}</strong>
  </div>
</div>`, t)
  },
  // 6. PROMOTIE (pastrat dar imbunatatit)
  {
    name: 'Promotie', icon: '🎉',
    fields: [
      { key: 'company', label: 'Magazin', defaultValue: 'Fashion Store' },
      { key: 'headline', label: 'Titlu promotie', defaultValue: 'MEGA REDUCERI' },
      { key: 'discount', label: 'Reducere principala', defaultValue: '50%' },
      { key: 'description', label: 'Descriere', defaultValue: 'Cele mai mari reduceri din acest sezon la toate colectiile!' },
      { key: 'prod1', label: 'Produs 1', defaultValue: '👗 Rochii de vara - de la 99 EUR' },
      { key: 'prod2', label: 'Produs 2', defaultValue: '👟 Incaltaminte sport - de la 149 EUR' },
      { key: 'prod3', label: 'Produs 3', defaultValue: '🎒 Genti & Accesorii - de la 79 EUR' },
      { key: 'code', label: 'Cod reducere', defaultValue: 'MEGA50' },
      { key: 'expiry', label: 'Valabil pana la', defaultValue: '31 Martie 2026' },
      { key: 'button_link', label: 'Link magazin', defaultValue: 'https://example.com/sale' },
    ],
    generate: (v, t) => generateBaseHtml(`
<div style="background:linear-gradient(135deg,${t.accent},${t.primary});padding:40px 30px;border-radius:12px 12px 0 0;color:white;text-align:center;">
  <p style="margin:0;font-size:14px;opacity:0.8;letter-spacing:3px;">${v.company}</p>
  <h1 style="margin:8px 0;font-size:36px;letter-spacing:2px;">${v.headline}</h1>
  <div style="font-size:72px;font-weight:900;margin:8px 0;text-shadow:2px 2px 4px rgba(0,0,0,0.3);">-${v.discount}</div>
  <p style="margin:0;font-size:15px;opacity:0.9;">${v.description}</p>
</div>
<div style="background:white;padding:28px;border-radius:0 0 12px 12px;box-shadow:0 4px 20px rgba(0,0,0,0.08);">
  <h3 style="color:${t.primary};margin:0 0 12px;font-size:15px;">Oferte speciale:</h3>
  ${[v.prod1, v.prod2, v.prod3].filter(Boolean).map(p => `<div style="padding:12px;margin:6px 0;background:${t.bg};border-radius:8px;font-size:14px;display:flex;align-items:center;">${p}</div>`).join('')}
  <div style="text-align:center;margin:24px 0;">
    <div style="display:inline-block;background:${t.bg};border:2px dashed ${t.accent};border-radius:10px;padding:14px 28px;">
      <div style="font-size:11px;color:#999;margin-bottom:4px;">Foloseste codul:</div>
      <div style="font-size:28px;font-weight:900;color:${t.primary};letter-spacing:6px;">${v.code}</div>
    </div>
    <p style="color:#999;font-size:12px;margin-top:8px;">Valabil pana la: ${v.expiry}</p>
  </div>
  <div style="text-align:center;">
    <a href="${v.button_link}" style="display:inline-block;padding:16px 48px;background:linear-gradient(135deg,${t.accent},${t.primary});color:white;text-decoration:none;border-radius:10px;font-weight:700;font-size:16px;box-shadow:0 4px 16px ${t.accent}44;">Cumpara Acum 🛍️</a>
  </div>
</div>`, t)
  },
];

interface SavedTemplate {
  id: string;
  name: string;
  html: string;
  savedAt: string;
}

const STORAGE_KEY = 'email_generator_saved_templates';

const loadSavedTemplates = (): SavedTemplate[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch { return []; }
};

const saveSavedTemplates = (templates: SavedTemplate[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
};

const EmailGenerator: React.FC = () => {
  const [selectedTheme, setSelectedTheme] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [values, setValues] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState('');
  const [copied, setCopied] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState<SavedTemplate[]>(loadSavedTemplates);
  const [saveName, setSaveName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [editingHtml, setEditingHtml] = useState('');
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [activeSection, setActiveSection] = useState<'predefined' | 'saved' | 'custom'>('predefined');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const template = TEMPLATES[selectedTemplate];
  const theme = THEMES[selectedTheme];

  React.useEffect(() => {
    if (activeSection === 'predefined') {
      const defaults: Record<string, string> = {};
      template.fields.forEach(f => { defaults[f.key] = f.defaultValue; });
      const saved = loadFieldValues(selectedTemplate);
      setValues(saved || defaults);
    }
  }, [selectedTemplate, activeSection, template]);

  // Persist field values to localStorage
  const saveFieldValues = (templateIdx: number, values: Record<string, string>) => {
    try {
      const saved = JSON.parse(localStorage.getItem('generator_fields') || '{}');
      saved[templateIdx] = values;
      localStorage.setItem('generator_fields', JSON.stringify(saved));
    } catch {}
  };

  const loadFieldValues = (templateIdx: number): Record<string, string> | null => {
    try {
      const saved = JSON.parse(localStorage.getItem('generator_fields') || '{}');
      return saved[templateIdx] || null;
    } catch { return null; }
  };

  React.useEffect(() => {
    if (activeSection === 'predefined' && template && Object.keys(values).length > 0) {
      setPreview(template.generate(values, theme));
    }
  }, [values, theme, template, activeSection]);

  const copyHtml = () => {
    navigator.clipboard.writeText(preview);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const useInCompose = () => {
    sessionStorage.setItem('emailGeneratorContent', preview);
    sessionStorage.setItem('emailGeneratorSubject', activeSection === 'predefined' ? template.name : 'Email personalizat');
    window.dispatchEvent(new CustomEvent('useGeneratedEmail'));
  };

  // Save current preview as template
  const saveCurrentAsTemplate = () => {
    if (!saveName.trim()) return;
    const newTemplate: SavedTemplate = {
      id: Date.now().toString(),
      name: saveName.trim(),
      html: preview,
      savedAt: new Date().toISOString()
    };
    const updated = [...savedTemplates, newTemplate];
    setSavedTemplates(updated);
    saveSavedTemplates(updated);
    setSaveName('');
    setShowSaveForm(false);
  };

  // Delete saved template
  const deleteSavedTemplate = (id: string) => {
    const updated = savedTemplates.filter(t => t.id !== id);
    setSavedTemplates(updated);
    saveSavedTemplates(updated);
  };

  // Load saved template into preview
  const loadSavedTemplate = (tmpl: SavedTemplate) => {
    setPreview(tmpl.html);
    setEditingHtml(tmpl.html);
    setActiveSection('saved');
  };

  // Update saved template
  const updateSavedTemplate = (id: string) => {
    const updated = savedTemplates.map(t => t.id === id ? { ...t, html: preview, savedAt: new Date().toISOString() } : t);
    setSavedTemplates(updated);
    saveSavedTemplates(updated);
  };

  // Import HTML from file
  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const html = ev.target?.result as string;
      setPreview(html);
      setEditingHtml(html);
      setActiveSection('custom');
      setShowHtmlEditor(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Apply HTML from editor
  const applyHtmlEdit = () => {
    setPreview(editingHtml);
    setShowHtmlEditor(false);
  };

  const btnStyle = (active: boolean) => ({
    padding: '8px 16px', border: active ? `2px solid ${theme.accent}` : '1px solid var(--border-color, #334155)',
    borderRadius: '8px', cursor: 'pointer' as const, fontSize: '13px', fontWeight: 600 as const,
    backgroundColor: active ? `${theme.accent}22` : 'var(--card-bg, #1e293b)',
    color: active ? theme.accent : 'var(--text-primary, #e2e8f0)', transition: 'all 0.2s'
  });

  return (
    <div>
      <h2 style={{ margin: '0 0 16px', fontSize: '20px' }}>Generator Email HTML</h2>

      {/* Section tabs */}
      <div className="section-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button className="section-tab-btn" onClick={() => setActiveSection('predefined')} style={btnStyle(activeSection === 'predefined')}>
          📋 Template-uri Predefinite
        </button>
        <button className="section-tab-btn" onClick={() => setActiveSection('saved')} style={btnStyle(activeSection === 'saved')}>
          💾 Salvate ({savedTemplates.length})
        </button>
        <button className="section-tab-btn" onClick={() => { setActiveSection('custom'); setShowHtmlEditor(true); setEditingHtml(preview || ''); }} style={btnStyle(activeSection === 'custom')}>
          ✏️ HTML Custom
        </button>
        <button className="section-tab-btn" onClick={() => fileInputRef.current?.click()} style={{
          ...btnStyle(false), border: `1px dashed var(--border-color, #334155)`
        }}>
          📂 Incarca HTML
        </button>
        <input ref={fileInputRef} type="file" accept=".html,.htm,.txt" onChange={handleFileImport} style={{ display: 'none' }} />
      </div>

      <div className="generator-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Left - Settings */}
        <div>
          {/* Theme selector - always visible */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>Tema culori</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px' }}>
              {THEMES.map((t, i) => (
                <button key={i} onClick={() => setSelectedTheme(i)} title={t.name} style={{
                  padding: '8px', border: selectedTheme === i ? `2px solid ${t.accent}` : '2px solid transparent',
                  borderRadius: '8px', cursor: 'pointer', background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`,
                  height: '36px', transition: 'all 0.2s'
                }} />
              ))}
            </div>
            <div style={{ fontSize: '12px', marginTop: '4px', color: 'var(--text-secondary, #94a3b8)' }}>{THEMES[selectedTheme].name}</div>
          </div>

          {activeSection === 'predefined' && (
            <>
              {/* Template selector */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>Tip template</label>
                <div className="template-selector-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                  {TEMPLATES.map((t, i) => (
                    <button key={i} onClick={() => setSelectedTemplate(i)} style={{
                      padding: '10px 8px', border: selectedTemplate === i ? `2px solid ${theme.accent}` : '1px solid var(--border-color, #334155)',
                      borderRadius: '8px', cursor: 'pointer', backgroundColor: selectedTemplate === i ? `${theme.accent}22` : 'var(--card-bg, #1e293b)',
                      color: 'var(--text-primary, #e2e8f0)', fontSize: '12px', textAlign: 'center', transition: 'all 0.2s'
                    }}>
                      <div style={{ fontSize: '20px' }}>{t.icon}</div>
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fields */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>Personalizare</label>
                {template.fields.map(field => (
                  <div key={field.key} style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '3px', color: 'var(--text-secondary, #94a3b8)' }}>{field.label}</label>
                    <input
                      value={values[field.key] || ''}
                      onChange={e => { const nv = { ...values, [field.key]: e.target.value }; setValues(nv); saveFieldValues(selectedTemplate, nv); }}
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: '6px',
                        border: '1px solid var(--border-color, #334155)',
                        backgroundColor: 'var(--input-bg, #0f172a)', color: 'var(--text-primary, #e2e8f0)',
                        fontSize: '13px', boxSizing: 'border-box'
                      }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {activeSection === 'saved' && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>Template-uri salvate</label>
              {savedTemplates.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontSize: '13px', border: '1px dashed var(--border-color, #334155)', borderRadius: '8px' }}>
                  Niciun template salvat. Creeaza unul si apasa "Salveaza".
                </div>
              ) : (
                savedTemplates.map(tmpl => (
                  <div key={tmpl.id} style={{
                    padding: '12px', marginBottom: '8px', borderRadius: '8px',
                    border: '1px solid var(--border-color, #334155)', backgroundColor: 'var(--card-bg, #1e293b)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>{tmpl.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', marginTop: '2px' }}>
                        Salvat: {new Date(tmpl.savedAt).toLocaleDateString('ro-RO')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => loadSavedTemplate(tmpl)} style={{
                        padding: '5px 10px', fontSize: '12px', border: 'none', borderRadius: '6px',
                        backgroundColor: theme.accent, color: 'white', cursor: 'pointer'
                      }}>Incarca</button>
                      <button onClick={() => { loadSavedTemplate(tmpl); setShowHtmlEditor(true); setActiveSection('custom'); }} style={{
                        padding: '5px 10px', fontSize: '12px', border: '1px solid var(--border-color, #334155)',
                        borderRadius: '6px', backgroundColor: 'transparent', color: 'var(--text-primary, #e2e8f0)', cursor: 'pointer'
                      }}>Editeaza</button>
                      <button onClick={() => updateSavedTemplate(tmpl.id)} style={{
                        padding: '5px 10px', fontSize: '12px', border: '1px solid #27ae60',
                        borderRadius: '6px', backgroundColor: 'transparent', color: '#27ae60', cursor: 'pointer'
                      }}>Actualizeaza</button>
                      <button onClick={() => deleteSavedTemplate(tmpl.id)} style={{
                        padding: '5px 10px', fontSize: '12px', border: 'none', borderRadius: '6px',
                        backgroundColor: '#e74c3c22', color: '#e74c3c', cursor: 'pointer'
                      }}>Sterge</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {(activeSection === 'custom' || showHtmlEditor) && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>Editor HTML</label>
              <textarea
                value={editingHtml}
                onChange={e => setEditingHtml(e.target.value)}
                placeholder="Lipeste sau scrie codul HTML aici..."
                style={{
                  width: '100%', height: '300px', padding: '12px', borderRadius: '8px',
                  border: '1px solid var(--border-color, #334155)',
                  backgroundColor: '#0f172a', color: '#22d3ee',
                  fontSize: '12px', fontFamily: 'monospace', boxSizing: 'border-box',
                  resize: 'vertical', lineHeight: '1.5'
                }}
              />
              <button onClick={applyHtmlEdit} style={{
                marginTop: '8px', padding: '8px 20px', border: 'none', borderRadius: '6px',
                backgroundColor: '#27ae60', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '13px', width: '100%'
              }}>Aplica HTML</button>
            </div>
          )}

          {/* Actions - always visible */}
          <div style={{ display: 'flex', gap: '6px', marginTop: '16px', flexWrap: 'wrap' }}>
            <button onClick={copyHtml} style={{
              flex: 1, minWidth: '100px', padding: '10px', border: 'none', borderRadius: '8px',
              backgroundColor: copied ? '#27ae60' : theme.accent, color: 'white',
              cursor: 'pointer', fontWeight: 600, fontSize: '13px', transition: 'all 0.2s'
            }}>{copied ? 'Copiat!' : 'Copiaza HTML'}</button>
            <button onClick={useInCompose} style={{
              flex: 1, minWidth: '100px', padding: '10px', border: `1px solid ${theme.accent}`,
              borderRadius: '8px', backgroundColor: 'transparent', color: theme.accent,
              cursor: 'pointer', fontWeight: 600, fontSize: '13px'
            }}>Foloseste in Email</button>
            <button onClick={() => { setShowSaveForm(true); setSaveName(''); }} style={{
              flex: 1, minWidth: '100px', padding: '10px', border: '1px solid #27ae60',
              borderRadius: '8px', backgroundColor: 'transparent', color: '#27ae60',
              cursor: 'pointer', fontWeight: 600, fontSize: '13px'
            }}>💾 Salveaza</button>
            <button onClick={() => setShowCode(!showCode)} style={{
              padding: '10px', border: '1px solid var(--border-color, #334155)',
              borderRadius: '8px', backgroundColor: 'transparent', color: 'var(--text-secondary, #94a3b8)',
              cursor: 'pointer', fontSize: '13px'
            }}>{showCode ? 'Preview' : 'Cod'}</button>
          </div>

          {/* Save form */}
          {showSaveForm && (
            <div style={{ marginTop: '12px', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color, #334155)', backgroundColor: 'var(--card-bg, #1e293b)' }}>
              <input
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                placeholder="Nume template..."
                onKeyDown={e => e.key === 'Enter' && saveCurrentAsTemplate()}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: '6px', marginBottom: '8px',
                  border: '1px solid var(--border-color, #334155)',
                  backgroundColor: 'var(--input-bg, #0f172a)', color: 'var(--text-primary, #e2e8f0)',
                  fontSize: '13px', boxSizing: 'border-box'
                }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={saveCurrentAsTemplate} style={{
                  flex: 1, padding: '8px', border: 'none', borderRadius: '6px',
                  backgroundColor: '#27ae60', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '13px'
                }}>Salveaza</button>
                <button onClick={() => setShowSaveForm(false)} style={{
                  padding: '8px 16px', border: '1px solid var(--border-color, #334155)', borderRadius: '6px',
                  backgroundColor: 'transparent', color: 'var(--text-secondary, #94a3b8)', cursor: 'pointer', fontSize: '13px'
                }}>Anuleaza</button>
              </div>
            </div>
          )}
        </div>

        {/* Right - Preview */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
            {showCode ? 'Cod HTML' : 'Preview'}
          </label>
          {showCode ? (
            <textarea
              value={preview}
              readOnly
              style={{
                width: '100%', height: '600px', padding: '12px', borderRadius: '12px',
                border: '1px solid var(--border-color, #334155)',
                backgroundColor: '#0f172a', color: '#22d3ee',
                fontSize: '11px', fontFamily: 'monospace', boxSizing: 'border-box',
                resize: 'none', lineHeight: '1.4'
              }}
            />
          ) : (
            <div style={{
              border: '1px solid var(--border-color, #334155)', borderRadius: '12px',
              overflow: 'hidden', backgroundColor: '#f5f5f5', minHeight: '500px'
            }}>
              <iframe
                srcDoc={preview}
                title="Email Preview"
                style={{ width: '100%', height: '600px', border: 'none' }}
                sandbox="allow-same-origin"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailGenerator;
